<script setup>
import StarterKit from '@tiptap/starter-kit';
import TiptapImage from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle, Color } from '@tiptap/extension-text-style';
import { Extension } from '@tiptap/core';
import { onBeforeUnmount, ref, watch } from 'vue';
import { useToastNotificacao } from 'nemesiselements';

const { erro: toastErro } = useToastNotificacao();
import { useEditor, EditorContent, VueNodeViewRenderer } from '@tiptap/vue-3';
import ImageNodeView from './ImageNodeView.vue';

// Ao pressionar Enter, o novo parágrafo sempre inicia com alinhamento à esquerda
const ResetAlignOnEnter = Extension.create({
    name: 'resetAlignOnEnter',
    addKeyboardShortcuts() {
        return {
            Enter: ({ editor }) => {
                const { selection, schema } = editor.state;
                const { $from } = selection;
                const node = $from.parent;
                if (node.type !== schema.nodes.paragraph && node.type !== schema.nodes.heading) return false;
                if (!node.attrs.textAlign || node.attrs.textAlign === 'left') return false;
                // deixa o StarterKit criar o novo parágrafo e depois reseta o alinhamento
                editor.chain().splitBlock().setTextAlign('left').run();
                return true;
            },
        };
    },
});

const props = defineProps({
    modelValue: {
        type: String,
        default: '',
    },
    placeholder: {
        type: String,
        default: 'Digite "/" para comandos ou comece a escrever...',
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    comImagem: {
        type: Boolean,
        default: true,
    },
    erro: {
        type: Boolean,
        default: false,
    },
    minHeight: {
        type: String,
        default: '26rem',
    },
});

const emit = defineEmits(['update:modelValue']);

// --- Bubble menu state ---
const bubbleMenu = ref({ visible: false, x: 0, y: 0 });
const bubbleMenuEl = ref(null);
const BUBBLE_MENU_WIDTH = 380; // largura estimada para clamp antes de medir

function updateBubbleMenu(ed) {
    const { empty, from, to } = ed.state.selection;
    if (empty || from === to) {
        bubbleMenu.value.visible = false;
        colorPickerOpen.value = false;
        return;
    }
    const start = ed.view.coordsAtPos(from);
    const editorEl = ed.view.dom.closest('.editor-texto');
    const rect = editorEl?.getBoundingClientRect() ?? { top: 0, left: 0, width: 9999 };
    const editorWidth = rect.width;

    // centro da seleção relativo ao editor
    const end = ed.view.coordsAtPos(to);
    const rawMidX = (start.left + end.left) / 2 - rect.left;
    const topY = start.top - rect.top - 44;

    // mede a largura real do menu se já estiver no DOM
    const menuWidth = bubbleMenuEl.value?.offsetWidth ?? BUBBLE_MENU_WIDTH;
    const half = menuWidth / 2;

    // clamp: garante que o menu não saia pelas bordas esquerda/direita
    const clampedX = Math.min(Math.max(half + 4, rawMidX), editorWidth - half - 4);

    bubbleMenu.value = { visible: true, x: clampedX, y: Math.max(4, topY) };
}

// --- Text color ---
const TEXT_COLORS = [
    { label: 'Padrão', value: null },
    { label: 'Preto', value: '#111827' },
    { label: 'Cinza escuro', value: '#374151' },
    { label: 'Cinza', value: '#6b7280' },
    { label: 'Vermelho', value: '#ef4444' },
    { label: 'Laranja', value: '#f97316' },
    { label: 'Amarelo', value: '#eab308' },
    { label: 'Verde', value: '#22c55e' },
    { label: 'Azul', value: '#3b82f6' },
    { label: 'Roxo', value: '#a855f7' },
    { label: 'Rosa', value: '#ec4899' },
];
const colorPickerOpen = ref(false);

function applyColor(value) {
    const ed = editor.value;
    if (!ed) return;
    if (value) {
        ed.chain().focus().setColor(value).run();
    } else {
        ed.chain().focus().unsetColor().run();
    }
    colorPickerOpen.value = false;
}

function currentColor() {
    return editor.value?.getAttributes('textStyle')?.color ?? null;
}

// --- Slash menu state ---
const slashMenu = ref({ visible: false, x: 0, y: 0, filter: '', selectedIndex: 0 });
const slashMenuEl = ref(null);
let slashStartPos = null;

const slashCommands = [
    { label: 'Texto', description: 'Parágrafo simples', icon: 'notes', action: (e) => e.chain().focus().setParagraph().run() },
    { label: 'Título 1', description: 'Título grande', icon: 'title', action: (e) => e.chain().focus().toggleHeading({ level: 1 }).run() },
    { label: 'Título 2', description: 'Título médio', icon: 'title', action: (e) => e.chain().focus().toggleHeading({ level: 2 }).run() },
    { label: 'Título 3', description: 'Título pequeno', icon: 'title', action: (e) => e.chain().focus().toggleHeading({ level: 3 }).run() },
    { label: 'Lista com marcadores', description: 'Lista simples', icon: 'format_list_bulleted', action: (e) => e.chain().focus().toggleBulletList().run() },
    { label: 'Lista numerada', description: 'Lista ordenada', icon: 'format_list_numbered', action: (e) => e.chain().focus().toggleOrderedList().run() },
    { label: 'Citação', description: 'Bloco de citação', icon: 'format_quote', action: (e) => e.chain().focus().toggleBlockquote().run() },
    { label: 'Linha horizontal', description: 'Divisor de seção', icon: 'horizontal_rule', action: (e) => e.chain().focus().setHorizontalRule().run() },
    { label: 'Imagem por URL', description: 'Inserir imagem via link', icon: 'image', action: () => insertImageUrl() },
    { label: 'Imagem do computador', description: 'Fazer upload de imagem', icon: 'upload', action: () => triggerImageUpload() },
];

const filteredCommands = ref([...slashCommands]);

function updateFilter(text) {
    const q = text.toLowerCase();
    const available = props.comImagem ? slashCommands : slashCommands.filter((c) => c.icon !== 'image' && c.icon !== 'upload');
    filteredCommands.value = available.filter(
        (c) => c.label.toLowerCase().includes(q) || c.description.toLowerCase().includes(q),
    );
    slashMenu.value.selectedIndex = 0;
}

function closeSlashMenu() {
    slashMenu.value.visible = false;
    slashStartPos = null;
}

function runSlashCommand(cmd) {
    if (!editor.value || slashStartPos === null) return;
    const { from } = editor.value.state.selection;
    editor.value.chain().focus().deleteRange({ from: slashStartPos, to: from }).run();
    cmd.action(editor.value);
    closeSlashMenu();
}

// --- Alignment (text + image) ---
function setAlign(value) {
    const ed = editor.value;
    if (!ed) return;
    if (ed.isActive('image')) {
        ed.chain().focus().updateAttributes('image', { align: value }).run();
    } else {
        ed.chain().focus().setTextAlign(value).run();
    }
}

function isAlignActive(value) {
    const ed = editor.value;
    if (!ed) return false;
    if (ed.isActive('image')) {
        const { align } = ed.getAttributes('image');
        return (align ?? 'left') === value;
    }
    return ed.isActive({ textAlign: value });
}

// --- Image upload ---
const imageUploadInput = ref(null);

function triggerImageUpload() {
    imageUploadInput.value?.click();
}

const IMAGE_MAX_SIZE_MB = 8;

function onImageUpload(event) {
    const file = event.target.files?.[0];
    if (!file || !editor.value) return;
    if (file.size > IMAGE_MAX_SIZE_MB * 1024 * 1024) {
        toastErro(`Imagem muito grande. O arquivo excede o limite de ${IMAGE_MAX_SIZE_MB} MB.`);
        event.target.value = '';
        return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
        editor.value.chain().focus().setImage({ src: e.target.result }).run();
    };
    reader.readAsDataURL(file);
    event.target.value = '';
}

// --- Diálogo de URL de imagem ---
const imageUrlDialogVisible = ref(false);
const imageUrlInput = ref('');

function insertImageUrl() {
    imageUrlInput.value = '';
    imageUrlDialogVisible.value = true;
}

function confirmImageUrl() {
    const url = imageUrlInput.value.trim();
    if (url) editor.value?.chain().focus().setImage({ src: url }).run();
    imageUrlDialogVisible.value = false;
}

// --- Editor setup ---
const editor = useEditor({
    content: props.modelValue,
    editable: !props.disabled,
    extensions: [
        StarterKit,
        TiptapImage.extend({
            addAttributes() {
                return {
                    ...this.parent?.(),
                    align: { default: 'left' },
                };
            },
            addNodeView() {
                return VueNodeViewRenderer(ImageNodeView);
            },
        }).configure({ inline: false, allowBase64: true }),
        Placeholder.configure({
            placeholder: ({ node }) => {
                if (node.type.name === 'heading') {
                    const levels = { 1: 'Título 1', 2: 'Título 2', 3: 'Título 3' };
                    return levels[node.attrs.level] ?? 'Título';
                }
                return props.placeholder;
            },
        }),
        TextAlign.configure({ types: ['heading', 'paragraph'], alignments: ['left', 'center', 'right', 'justify'] }),
        TextStyle,
        Color,
        ResetAlignOnEnter,
    ],
    onUpdate: ({ editor }) => {
        emit('update:modelValue', editor.getHTML());
    },
    onSelectionUpdate: ({ editor }) => {
        updateBubbleMenu(editor);
    },
    onBlur: () => {
        bubbleMenu.value.visible = false;
    },
    onKeyDown: ({ event }) => {
        if (slashMenu.value.visible) {
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                slashMenu.value.selectedIndex = (slashMenu.value.selectedIndex + 1) % filteredCommands.value.length;
                return true;
            }
            if (event.key === 'ArrowUp') {
                event.preventDefault();
                slashMenu.value.selectedIndex =
                    (slashMenu.value.selectedIndex - 1 + filteredCommands.value.length) % filteredCommands.value.length;
                return true;
            }
            if (event.key === 'Enter') {
                event.preventDefault();
                const cmd = filteredCommands.value[slashMenu.value.selectedIndex];
                if (cmd) runSlashCommand(cmd);
                return true;
            }
            if (event.key === 'Escape') {
                closeSlashMenu();
                return true;
            }
        }
        return false;
    },
});

// Detect "/" to open slash menu
watch(
    () => editor.value,
    (ed) => {
        if (!ed) return;
        ed.on('transaction', () => {
            if (!ed.isFocused) return;
            const { from } = ed.state.selection;
            const text = ed.state.doc.textBetween(Math.max(0, from - 50), from, '\n');
            const slashIdx = text.lastIndexOf('/');
            if (slashIdx !== -1) {
                const after = text.slice(slashIdx + 1);
                if (!after.includes(' ') && !after.includes('\n')) {
                    slashStartPos = from - after.length - 1;
                    updateFilter(after);
                    // position menu near cursor
                    const coords = ed.view.coordsAtPos(from);
                    const editorEl = ed.view.dom.closest('.editor-texto');
                    const rect = editorEl?.getBoundingClientRect() ?? { top: 0, left: 0 };
                    slashMenu.value = {
                        visible: true,
                        x: coords.left - rect.left,
                        y: coords.bottom - rect.top + 4,
                        filter: after,
                        selectedIndex: slashMenu.value.selectedIndex,
                    };
                    return;
                }
            }
            if (slashMenu.value.visible) closeSlashMenu();
        });
    },
    { immediate: true },
);

watch(
    () => props.modelValue,
    (val) => {
        if (editor.value && editor.value.getHTML() !== val) {
            editor.value.commands.setContent(val || '', false);
        }
    },
);

watch(
    () => props.disabled,
    (val) => {
        editor.value?.setEditable(!val);
    },
);

onBeforeUnmount(() => {
    editor.value?.destroy();
});
</script>

<template>
    <div class="editor-texto border-1 border-round-lg overflow-hidden"
        :class="{ 'editor-texto--disabled': disabled, 'invalid-border-error': props.erro, 'surface-border': !props.erro }">
        <!-- Toolbar fixa -->
        <div v-if="editor && !disabled"
            class="editor-texto__toolbar flex flex-wrap justify-content-center align-items-center gap-1 px-2 py-2 border-bottom-1 surface-border surface-section">
            <!-- Histórico -->
            <button type="button" class="editor-texto__btn" title="Desfazer" :disabled="!editor.can().undo()"
                @click="editor.chain().focus().undo().run()">
                <span class="material-symbols-rounded">undo</span>
            </button>
            <button type="button" class="editor-texto__btn" title="Refazer" :disabled="!editor.can().redo()"
                @click="editor.chain().focus().redo().run()">
                <span class="material-symbols-rounded">redo</span>
            </button>

            <span class="editor-texto__separator" />

            <!-- Tipo de bloco -->
            <button type="button" class="editor-texto__btn editor-texto__btn--text"
                :class="{ 'editor-texto__btn--active': editor.isActive('heading', { level: 1 }) }" title="Título 1"
                @click="editor.chain().focus().toggleHeading({ level: 1 }).run()">H1</button>
            <button type="button" class="editor-texto__btn editor-texto__btn--text"
                :class="{ 'editor-texto__btn--active': editor.isActive('heading', { level: 2 }) }" title="Título 2"
                @click="editor.chain().focus().toggleHeading({ level: 2 }).run()">H2</button>
            <button type="button" class="editor-texto__btn editor-texto__btn--text"
                :class="{ 'editor-texto__btn--active': editor.isActive('heading', { level: 3 }) }" title="Título 3"
                @click="editor.chain().focus().toggleHeading({ level: 3 }).run()">H3</button>

            <span class="editor-texto__separator" />

            <!-- Listas -->
            <button type="button" class="editor-texto__btn"
                :class="{ 'editor-texto__btn--active': editor.isActive('bulletList') }" title="Lista com marcadores"
                @click="editor.chain().focus().toggleBulletList().run()">
                <span class="material-symbols-rounded">format_list_bulleted</span>
            </button>
            <button type="button" class="editor-texto__btn"
                :class="{ 'editor-texto__btn--active': editor.isActive('orderedList') }" title="Lista numerada"
                @click="editor.chain().focus().toggleOrderedList().run()">
                <span class="material-symbols-rounded">format_list_numbered</span>
            </button>

            <span class="editor-texto__separator" />

            <!-- Alinhamento -->
            <button type="button" class="editor-texto__btn"
                :class="{ 'editor-texto__btn--active': isAlignActive('left') }" title="Alinhar à esquerda"
                @click="setAlign('left')">
                <span class="material-symbols-rounded">format_align_left</span>
            </button>
            <button type="button" class="editor-texto__btn"
                :class="{ 'editor-texto__btn--active': isAlignActive('center') }" title="Centralizar"
                @click="setAlign('center')">
                <span class="material-symbols-rounded">format_align_center</span>
            </button>
            <button type="button" class="editor-texto__btn"
                :class="{ 'editor-texto__btn--active': isAlignActive('right') }" title="Alinhar à direita"
                @click="setAlign('right')">
                <span class="material-symbols-rounded">format_align_right</span>
            </button>
            <button type="button" class="editor-texto__btn"
                :class="{ 'editor-texto__btn--active': isAlignActive('justify') }" title="Justificar"
                @click="setAlign('justify')">
                <span class="material-symbols-rounded">format_align_justify</span>
            </button>

            <span class="editor-texto__separator" />

            <!-- Imagem -->
            <template v-if="comImagem">
                <button type="button" class="editor-texto__btn" title="Inserir imagem por URL" @click="insertImageUrl()">
                    <span class="material-symbols-rounded">image</span>
                </button>
                <button type="button" class="editor-texto__btn" title="Fazer upload de imagem"
                    @click="triggerImageUpload()">
                    <span class="material-symbols-rounded">upload</span>
                </button>

                <span class="editor-texto__separator" />
            </template>

            <!-- Blocos extras -->
            <button type="button" class="editor-texto__btn"
                :class="{ 'editor-texto__btn--active': editor.isActive('blockquote') }" title="Citação"
                @click="editor.chain().focus().toggleBlockquote().run()">
                <span class="material-symbols-rounded">format_quote</span>
            </button>
            <button type="button" class="editor-texto__btn" title="Linha horizontal"
                @click="editor.chain().focus().setHorizontalRule().run()">
                <span class="material-symbols-rounded">horizontal_rule</span>
            </button>
        </div>

        <!-- Bubble menu ao selecionar texto -->
        <div v-if="editor && !disabled && bubbleMenu.visible" ref="bubbleMenuEl" class="editor-texto__bubble"
            :style="{ left: bubbleMenu.x + 'px', top: bubbleMenu.y + 'px' }" @mousedown.prevent>
            <button type="button" class="editor-texto__bubble-btn"
                :class="{ 'editor-texto__bubble-btn--active': editor.isActive('bold') }" title="Negrito"
                @click="editor.chain().focus().toggleBold().run()">
                <span class="material-symbols-rounded">format_bold</span>
            </button>
            <button type="button" class="editor-texto__bubble-btn"
                :class="{ 'editor-texto__bubble-btn--active': editor.isActive('italic') }" title="Itálico"
                @click="editor.chain().focus().toggleItalic().run()">
                <span class="material-symbols-rounded">format_italic</span>
            </button>
            <button type="button" class="editor-texto__bubble-btn"
                :class="{ 'editor-texto__bubble-btn--active': editor.isActive('strike') }" title="Tachado"
                @click="editor.chain().focus().toggleStrike().run()">
                <span class="material-symbols-rounded">strikethrough_s</span>
            </button>
            <button type="button" class="editor-texto__bubble-btn"
                :class="{ 'editor-texto__bubble-btn--active': editor.isActive('code') }" title="Código"
                @click="editor.chain().focus().toggleCode().run()">
                <span class="material-symbols-rounded">code</span>
            </button>

            <!-- Cor do texto -->
            <span class="editor-texto__bubble-sep" />
            <div class="editor-texto__color-wrap">
                <button type="button" class="editor-texto__bubble-btn editor-texto__color-btn"
                    title="Cor do texto" @click="colorPickerOpen = !colorPickerOpen">
                    <span class="material-symbols-rounded">format_color_text</span>
                    <span class="editor-texto__color-indicator" :style="{ background: currentColor() ?? 'transparent', border: currentColor() ? 'none' : '1px solid #ccc' }" />
                </button>
                <div v-if="colorPickerOpen" class="editor-texto__color-picker" @mousedown.stop>
                    <button v-for="c in TEXT_COLORS" :key="c.label" type="button"
                        class="editor-texto__color-swatch"
                        :class="{ 'editor-texto__color-swatch--active': currentColor() === c.value }"
                        :title="c.label"
                        :style="c.value ? { background: c.value } : {}"
                        @mousedown.prevent="applyColor(c.value)">
                        <span v-if="!c.value" class="material-symbols-rounded" style="font-size:14px;color:#374151">format_color_reset</span>
                    </button>
                </div>
            </div>

            <span class="editor-texto__bubble-sep" />
            <button type="button" class="editor-texto__bubble-btn"
                :class="{ 'editor-texto__bubble-btn--active': editor.isActive('heading', { level: 1 }) }" title="H1"
                @click="editor.chain().focus().toggleHeading({ level: 1 }).run()">
                <span class="editor-texto__bubble-label">H1</span>
            </button>
            <button type="button" class="editor-texto__bubble-btn"
                :class="{ 'editor-texto__bubble-btn--active': editor.isActive('heading', { level: 2 }) }" title="H2"
                @click="editor.chain().focus().toggleHeading({ level: 2 }).run()">
                <span class="editor-texto__bubble-label">H2</span>
            </button>
            <button type="button" class="editor-texto__bubble-btn"
                :class="{ 'editor-texto__bubble-btn--active': editor.isActive('heading', { level: 3 }) }" title="H3"
                @click="editor.chain().focus().toggleHeading({ level: 3 }).run()">
                <span class="editor-texto__bubble-label">H3</span>
            </button>
            <span class="editor-texto__bubble-sep" />
            <button type="button" class="editor-texto__bubble-btn"
                :class="{ 'editor-texto__bubble-btn--active': isAlignActive('left') }" title="Esquerda"
                @click="setAlign('left')">
                <span class="material-symbols-rounded">format_align_left</span>
            </button>
            <button type="button" class="editor-texto__bubble-btn"
                :class="{ 'editor-texto__bubble-btn--active': isAlignActive('center') }" title="Centro"
                @click="setAlign('center')">
                <span class="material-symbols-rounded">format_align_center</span>
            </button>
            <button type="button" class="editor-texto__bubble-btn"
                :class="{ 'editor-texto__bubble-btn--active': isAlignActive('right') }" title="Direita"
                @click="setAlign('right')">
                <span class="material-symbols-rounded">format_align_right</span>
            </button>
            <button type="button" class="editor-texto__bubble-btn"
                :class="{ 'editor-texto__bubble-btn--active': isAlignActive('justify') }" title="Justificar"
                @click="setAlign('justify')">
                <span class="material-symbols-rounded">format_align_justify</span>
            </button>
        </div>

        <!-- Slash menu -->
        <div v-if="slashMenu.visible && filteredCommands.length" ref="slashMenuEl" class="editor-texto__slash-menu"
            :style="{ left: slashMenu.x + 'px', top: slashMenu.y + 'px' }">
            <div class="editor-texto__slash-header">Blocos</div>
            <button v-for="(cmd, i) in filteredCommands" :key="cmd.label" type="button" class="editor-texto__slash-item"
                :class="{ 'editor-texto__slash-item--active': i === slashMenu.selectedIndex }"
                @mousedown.prevent="runSlashCommand(cmd)">
                <span class="editor-texto__slash-icon material-symbols-rounded">{{ cmd.icon }}</span>
                <div class="editor-texto__slash-info">
                    <span class="editor-texto__slash-label">{{ cmd.label }}</span>
                    <span class="editor-texto__slash-desc">{{ cmd.description }}</span>
                </div>
            </button>
        </div>

        <EditorContent :editor="editor" class="editor-texto__content" :style="{ '--editor-texto-min-height': minHeight }" />

        <!-- Input oculto para upload de imagem -->
        <input ref="imageUploadInput" type="file" accept="image/*" style="display: none" @change="onImageUpload" />

        <!-- Diálogo de URL de imagem -->
        <Dialog v-model:visible="imageUrlDialogVisible" header="Inserir imagem por URL" :modal="true" :draggable="false"
            style="width: 28rem" @keydown.enter.prevent="confirmImageUrl">
            <div class="flex flex-column gap-2 pt-1">
                <label for="image-url-input" class="text-sm font-medium">URL da imagem</label>
                <InputText id="image-url-input" v-model="imageUrlInput" placeholder="https://..." autofocus />
            </div>
            <template #footer>
                <Button label="Cancelar" text @click="imageUrlDialogVisible = false" />
                <Button label="Inserir" :disabled="!imageUrlInput.trim()" @click="confirmImageUrl" />
            </template>
        </Dialog>
    </div>
</template>

<style scoped>
.editor-texto {
    position: relative;
}

.editor-texto--disabled {
    opacity: 1;
    pointer-events: none;
    /* background: white */
}

/* ─── Toolbar ─────────────────────────────────────────────── */
.editor-texto__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border: none;
    border-radius: 5px;
    background: transparent;
    color: var(--text-color-secondary, #6c757d);
    cursor: pointer;
    font-size: 0.8rem;
    transition: background 0.15s, color 0.15s, transform 0.1s;
}

.editor-texto__btn .material-symbols-rounded {
    font-size: 18px;
    font-variation-settings: 'wght' 400;
    line-height: 1;
}

.editor-texto__btn--text {
    width: auto;
    padding: 0 7px;
    font-weight: 700;
    font-size: 0.72rem;
    letter-spacing: 0.02em;
}

.editor-texto__btn:hover:not(:disabled) {
    background: var(--surface-hover, #e9ecef);
    color: var(--text-color, #495057);
}

.editor-texto__btn:active:not(:disabled) {
    transform: scale(0.9);
}

.editor-texto__btn--active {
    background: color-mix(in srgb, var(--primary-color, #6366f1) 15%, transparent);
    color: var(--primary-color, #6366f1);
}

.editor-texto__btn--active:hover:not(:disabled) {
    background: color-mix(in srgb, var(--primary-color, #6366f1) 25%, transparent);
}

.editor-texto__btn:disabled {
    opacity: 0.3;
    cursor: not-allowed;
}

.editor-texto__separator {
    width: 1px;
    height: 18px;
    background: var(--surface-border, #dee2e6);
    margin: 0 0.25rem;
    flex-shrink: 0;
}

/* ─── Bubble menu ─────────────────────────────────────────── */
.editor-texto__bubble {
    position: absolute;
    z-index: 50;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 4px 6px;
    background: var(--surface-overlay, #fff);
    border: 1px solid var(--surface-border, #dee2e6);
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    white-space: nowrap;
    pointer-events: all;
}

.editor-texto__bubble-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 4px;
    background: transparent;
    color: var(--text-color-secondary, #6c757d);
    cursor: pointer;
    transition: background 0.12s, color 0.12s;
}

.editor-texto__bubble-btn .material-symbols-rounded {
    font-size: 17px;
    font-variation-settings: 'wght' 400;
}

.editor-texto__bubble-label {
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.02em;
}

.editor-texto__bubble-btn:hover {
    background: var(--surface-hover, #f3f4f6);
    color: var(--text-color, #495057);
}

.editor-texto__bubble-btn--active {
    background: color-mix(in srgb, var(--primary-color, #6366f1) 15%, transparent);
    color: var(--primary-color, #111118);
}

.editor-texto__bubble-sep {
    width: 1px;
    height: 16px;
    background: var(--surface-border, #dee2e6);
    margin: 0 2px;
    flex-shrink: 0;
}

/* ─── Color picker ────────────────────────────────────────── */
.editor-texto__color-wrap {
    position: relative;
}

.editor-texto__color-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1px;
    height: 28px;
    width: 28px;
}

.editor-texto__color-indicator {
    display: block;
    width: 14px;
    height: 3px;
    border-radius: 2px;
    margin-top: -2px;
}

.editor-texto__color-picker {
    position: absolute;
    top: calc(100% + 6px);
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 4px;
    padding: 6px;
    background: var(--surface-overlay, #fff);
    border: 1px solid var(--surface-border, #dee2e6);
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.14);
    z-index: 10;
    white-space: nowrap;
}

.editor-texto__color-swatch {
    width: 20px;
    height: 20px;
    border-radius: 4px;
    border: 2px solid transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.1s, border-color 0.1s;
    background-clip: padding-box;
}

.editor-texto__color-swatch:hover {
    transform: scale(1.2);
    border-color: var(--surface-border, #dee2e6);
}

.editor-texto__color-swatch--active {
    border-color: var(--primary-color, #6366f1) !important;
    transform: scale(1.15);
}

/* ─── Slash menu ──────────────────────────────────────────── */
.editor-texto__slash-menu {
    position: absolute;
    z-index: 100;
    min-width: 220px;
    max-height: 280px;
    overflow-y: auto;
    background: var(--surface-overlay, #fff);
    border: 1px solid var(--surface-border, #dee2e6);
    border-radius: 10px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    padding: 4px;
}

.editor-texto__slash-header {
    font-size: 0.68rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--text-color-secondary, #6c757d);
    padding: 6px 10px 4px;
}

.editor-texto__slash-item {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 7px 10px;
    border: none;
    border-radius: 6px;
    background: transparent;
    cursor: pointer;
    text-align: left;
    transition: background 0.1s;
}

.editor-texto__slash-item:hover,
.editor-texto__slash-item--active {
    background: var(--surface-hover, #f3f4f6);
}

.editor-texto__slash-icon {
    font-size: 18px;
    color: var(--text-color-secondary, #6c757d);
    flex-shrink: 0;
    font-variation-settings: 'wght' 300;
}

.editor-texto__slash-info {
    display: flex;
    flex-direction: column;
    gap: 1px;
}

.editor-texto__slash-label {
    font-size: 0.83rem;
    font-weight: 500;
    color: var(--text-color, #495057);
    line-height: 1.2;
}

.editor-texto__slash-desc {
    font-size: 0.72rem;
    color: var(--text-color-secondary, #9ca3af);
    line-height: 1.2;
}

/* ─── Content area ────────────────────────────────────────── */
.editor-texto__content :deep(.ProseMirror) {
    min-height: var(--editor-texto-min-height, 26rem);
    padding: 16px 20px;
    outline: none;
    font-size: 0.925rem;
    line-height: 1.75;
    color: var(--text-color, #181818);
}

.editor-texto__content :deep(.ProseMirror > * + *) {
    margin-top: 0.3em;
}

.editor-texto__content :deep(.ProseMirror p) {
    margin: 0;
}

/* Placeholder estilo Notion */
.editor-texto__content :deep(.ProseMirror .is-empty::before) {
    content: attr(data-placeholder);
    display: block;
    color: #c0bfbe;
    pointer-events: none;
    height: 0;
    font-style: normal;
    text-align: inherit;
}

.editor-texto__content :deep(.ProseMirror h1),
.editor-texto__content :deep(.ProseMirror h2),
.editor-texto__content :deep(.ProseMirror h3) {
    margin: 1.2em 0 0.2em;
    font-weight: 700;
    line-height: 1.3;
    color: #1e2023;
    letter-spacing: -0.01em;
}

.editor-texto__content :deep(.ProseMirror h1:first-child),
.editor-texto__content :deep(.ProseMirror h2:first-child),
.editor-texto__content :deep(.ProseMirror h3:first-child) {
    margin-top: 0;
}

.editor-texto__content :deep(.ProseMirror h1) {
    font-size: 1.75rem;
}

.editor-texto__content :deep(.ProseMirror h2) {
    font-size: 1.35rem;
}

.editor-texto__content :deep(.ProseMirror h3) {
    font-size: 1.1rem;
}

.editor-texto__content :deep(.ProseMirror ul),
.editor-texto__content :deep(.ProseMirror ol) {
    padding-left: 1.6rem;
    margin: 0.2rem 0;
}

.editor-texto__content :deep(.ProseMirror li) {
    margin-bottom: 0.15rem;
}

.editor-texto__content :deep(.ProseMirror li > p) {
    margin: 0;
}

.editor-texto__content :deep(.ProseMirror blockquote) {
    border-left: 3px solid #7d7d7d;
    padding: 0.4rem 0 0.4rem 1rem;
    margin: 0.5rem 0;
    color: var(--text-color-secondary, #6c757d);
    font-style: italic;
    background:
        color-mix(in srgb, #a6a6a6 5%, transparent);
    border-radius: 0 4px 4px 0;
}

.editor-texto__content :deep(.ProseMirror code) {
    background: var(--surface-ground, #f3f4f6);
    border: 1px solid var(--surface-border, #dee2e6);
    border-radius: 3px;
    padding: 0.1em 0.35em;
    font-size: 0.83em;
    font-family: monospace;
    color: var(--pink-600, #e83e8c);
}


.editor-texto__content :deep(.ProseMirror hr) {
    border: none;
    border-top: 1px solid var(--surface-border, #dee2e6);
    margin: 1.2rem 0;
}

.editor-texto__content :deep(.ProseMirror strong) {
    font-weight: 700;
}

.editor-texto__content :deep(.ProseMirror em) {
    font-style: italic;
}

.editor-texto__content :deep(.ProseMirror s) {
    text-decoration: line-through;
    opacity: 0.7;
}

/* Imagens — visual gerenciado pelo ImageNodeView */
.editor-texto__content :deep(.ProseMirror .image-node-view) {
    margin: 0.5rem 0;
}
</style>
